/*
 * @license
 * Copyright© Celligence International, LLC - 2023
 *
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 *
 */
import React, { useEffect, useState, Suspense, lazy } from "react";
import { useParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import { HelmetProvider } from "react-helmet-async";
import MetaTags from "../MetaTags";
import SpinnerLoader from "../SpinnerLoader";
import axios from "axios";
import { isMobileDevice, isMobileNotTabletDevice } from "../Utils";

const PreEventTemplates = lazy(() => import("../PreEventTemplates"));
const PostEventTemplates = lazy(() => import("../PostEventTemplates"));

export default function EventHomePage() {
    const { eventName } = useParams();
    const correctedEventName = eventName.toLowerCase();
    const [showPostEventPage, setShowPostEventPage] = useState(false);
    const [eventPageData, setEventPageData] = useState(null);
    const [preEventData, setPreEventData] = useState(null);
    const [postEventData, setPostEventData] = useState(null);
    const [metaTagsData, setMetaTagsData] = useState(null);
    const [isMobile, setIsMobile] = useState(isMobileDevice());
    const [isMobileNotTablet, setIsMobileNotTablet] = useState(isMobileNotTabletDevice());

    useEffect(() => {
        /**
         * @author Pratik Kajrolkar
         * @date 06/04/2024
         *
         * The below function is used to fetch the event data JSON from the AWS S3 bucket location.
         * @param {String} objectName the json object which will fetch from S3 bucket
         * @returns JSON Object of the requested file
        */
        const fetchData = async () => {
            try {
                const url = process.env.REACT_APP_EVENTS_PAGE_JSON + correctedEventName + ".json?t=" + new Date().getTime();

                const response = await axios.get(url);
                if (response.data) {
                    const eventData = response.data;
                    // Auto-detect MindsetSummit type if bannerV2 exists at root level without a type field
                    if (!eventData.type && eventData.bannerV2) {
                        eventData.type = "MindsetSummit";
                        eventData.eventCreatedUnderDomain = eventData.eventCreatedUnderDomain || "angelai";
                    }
                    // Batch state updates to avoid multiple re-renders
                    setEventPageData(eventData);
                    setMetaTagsData(eventData.metaTags || eventData.metatags);
                    setShowPostEventPage(eventData.showPostEventPage);
                    setPreEventData(eventData.preEvent || eventData);
                    setPostEventData(eventData.postEvent);
                } else {
                    window.location.href = process.env.PUBLIC_URL || '/events';
                }
            } catch (e) {
                console.log(e);
                window.location.href = process.env.PUBLIC_URL || '/events';
            }
        };

        fetchData();

        // Cleanup function to handle component unmounting
        return () => {
            // Cancel any pending requests if needed
        };
    }, [correctedEventName]);

    useEffect(() => {
        function handleResize() {
            setIsMobile(isMobileDevice());
            setIsMobileNotTablet(isMobileNotTabletDevice());
        }

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <HelmetProvider>
            <Container fluid className="ps-0 pe-0">
                {eventPageData ? 
                    <>
                        {metaTagsData && <MetaTags eventCreatedUnderDomain={eventPageData.eventCreatedUnderDomain} metaTagsData={metaTagsData} />}
                        <Suspense fallback={<SpinnerLoader />}>
                            {showPostEventPage ? 
                                <PostEventTemplates 
                                    eventCreatedUnderDomain={eventPageData.eventCreatedUnderDomain}
                                    eventPageData={eventPageData} 
                                    preEventData={preEventData}
                                    postEventData={postEventData} 
                                    isMobile={isMobile}
                                    isMobileNotTablet={isMobileNotTablet}
                                />
                            :
                                <PreEventTemplates 
                                    eventCreatedUnderDomain={eventPageData.eventCreatedUnderDomain}
                                    eventPageData={eventPageData} 
                                    preEventData={preEventData} 
                                    isMobile={isMobile}
                                    isMobileNotTablet={isMobileNotTablet}
                                />
                            }
                        </Suspense>
                    </>
                : 
                    <SpinnerLoader />
                }
            </Container>
        </HelmetProvider>
    );
};